import { useQuery } from '@tanstack/react-query'
import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline'
import { weeklyProgramsAPI } from '../../services/api'
import { StatusBadge } from './StatusBadge'
import { ScoreChip } from './ScoreChip'

export function ProgramCard({ program, isExpanded, onToggleExpand, onEdit, onDelete, onCopy, onSubmit, onViewReport, onViewDetail, t, i18n }) {
  const isGerman = i18n.language === 'de'
  const sessionsCount = program.sessions?.length || 0
  const completedSessions = program.sessions?.filter(s => s.is_completed).length || 0

  // Fetch score for submitted programs
  const { data: scoreData } = useQuery({
    queryKey: ['programScore', program.id],
    queryFn: () => weeklyProgramsAPI.getScore(program.id),
    enabled: program.status === 'submitted',
    staleTime: 5 * 60 * 1000,
    retry: false
  })
  const score = scoreData?.data

  return (
    <div className={`card hover:shadow-lg transition-shadow ${program.status === 'submitted' ? 'border-green-200 bg-green-50/50' : ''}`}>
      <div className="card-body">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <CalendarIcon className="h-6 w-6 text-primary-600 mr-2" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t('programs.week')} {program.week_number}, {program.year}
                </h3>
                <p className="text-sm text-gray-500">
                  {new Date(program.week_start_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit' })} - {new Date(program.week_end_date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              </div>
              <div className="ml-4">
                <StatusBadge status={program.status} t={t} />
              </div>
              {program.status === 'submitted' && score && (
                <div className="ml-2">
                  <ScoreChip
                    score={score.total_score}
                    showTrophy={score.is_personal_best}
                  />
                </div>
              )}
              {program.is_template && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  <BookmarkIcon className="h-3 w-3 mr-1" />
                  {t('programs.isTemplate')}
                </span>
              )}
            </div>

            {/* Stats row */}
            <div className="flex items-center space-x-6 mt-3 text-sm text-gray-600">
              <span className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1" />
                {completedSessions}/{sessionsCount} {t('programs.sessions')}
              </span>
              {program.weekly_notes && (
                <span className="text-gray-400 truncate max-w-xs">
                  {program.weekly_notes}
                </span>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onViewDetail}
              className="btn-primary btn-sm flex items-center"
              title={program.status === 'submitted' ? t('programs.viewResults') : t('programs.viewDetails')}
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              {program.status === 'submitted' ? t('programs.viewResults') : t('programs.viewDetails')}
            </button>
            {program.status !== 'submitted' && (
              <button
                onClick={onSubmit}
                className="btn-outline btn-sm flex items-center text-green-600 hover:bg-green-50"
                title={t('programs.submit')}
              >
                <CheckCircleIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onViewReport}
              className="btn-outline btn-sm flex items-center"
              title={t('programs.report.title')}
            >
              <ChartBarIcon className="h-4 w-4" />
            </button>
            <button
              onClick={onCopy}
              className="btn-outline btn-sm flex items-center"
              title={t('programs.copy')}
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
            <button
              onClick={onToggleExpand}
              className="btn-outline btn-sm"
            >
              {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Expanded Content - Sessions List */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {program.sessions && program.sessions.length > 0 ? (
              <div className="space-y-2">
                {program.sessions.map((session) => {
                  const skippedCount = session.exercises?.filter(e => e.is_skipped).length || 0
                  const unplannedCount = session.exercises?.filter(e => e.is_unplanned).length || 0
                  const totalExercises = session.exercises?.length || 0
                  // Count logged exercises (has actual results)
                  const loggedCount = session.exercises?.filter(e => {
                    if (e.is_skipped) return false
                    // Check if has set results with actual values
                    if (e.set_results && e.set_results.length > 0) {
                      return e.set_results.some(s => s.actual_reps != null || s.actual_weight != null)
                    }
                    // Check metric_values for non-per-set exercises
                    if (e.metric_values && Object.keys(e.metric_values).length > 0) {
                      return Object.values(e.metric_values).some(v => v != null && v !== '')
                    }
                    return false
                  }).length || 0
                  const activeExercises = totalExercises - skippedCount

                  return (
                    <div
                      key={session.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${session.is_completed ? 'bg-green-50' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center">
                        {session.is_completed ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                        ) : (
                          <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{session.snapshot_module_name_en}</p>
                          <p className="text-sm text-gray-500">
                            {session.planned_day && t(`programs.dayOfWeek.${session.planned_day}`)}
                            {session.exercises && ` - ${session.exercises.length} ${t('trainingDays.exercises')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Logged count badge - only show as green for submitted programs */}
                        {activeExercises > 0 && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            program.status === 'submitted'
                              ? (loggedCount === activeExercises
                                  ? 'bg-green-100 text-green-700'
                                  : loggedCount > 0
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-600')
                              : 'bg-blue-50 text-blue-600'  // Draft: show as "target" style
                          }`}>
                            {program.status === 'submitted' ? (
                              <CheckCircleIcon className="h-3 w-3 mr-0.5" />
                            ) : (
                              <ChartBarIcon className="h-3 w-3 mr-0.5" />
                            )}
                            {loggedCount}/{activeExercises}
                          </span>
                        )}
                        {skippedCount > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                            {skippedCount} {t('programs.skipped')}
                          </span>
                        )}
                        {unplannedCount > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-600">
                            +{unplannedCount}
                          </span>
                        )}
                        {session.snapshot_module_color && (
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: session.snapshot_module_color }}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                {t('templates.noExercises')}
              </p>
            )}

            {/* Actions Footer */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end items-center">
              <div className="flex space-x-2">
                {program.status !== 'submitted' && (
                  <>
                    <button
                      onClick={onEdit}
                      className="btn-outline btn-sm flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={onDelete}
                      className="btn-outline btn-sm flex items-center text-red-600 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      {t('common.delete')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
